import { useState } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { useAuth } from '@zep/auth';
import { logger } from '@zep/core-logger';
import { CheckIcon, ChevronLeftIcon, ExclamationmarkSmallCircleIcon } from '@zep/icons';
import { Button, Checkbox, Divider, Spacing } from '@zep/ui';
import { isApp } from '@zep/utils';
import { WithdrawInputModal } from '../../components/service/accounts/modals/WithdrawInputModal';
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}
export const WithdrawPageView = () => {
  const {
    t
  } = useTranslation(['accounts']);
  const router = useRouter();
  const [isAgreeWithdraw, setIsAgreeWithdraw] = useState(false);
  const withdrawInputModal = useModal(WithdrawInputModal);
  const {
    user
  } = useAuth();
  const handleClickGoBack = () => {
    if (isApp()) {
      window.ReactNativeWebView?.postMessage(JSON.stringify({
        message: 'GO_BACK'
      }));
      return;
    }
    router.back();
  };
  const onSuccessWithdraw = () => {
    logger.track('withdrawal_complete');
    if (isApp()) {
      window.ReactNativeWebView?.postMessage(JSON.stringify({
        message: 'ONSUCCESS_WITHDRAW'
      }));
    }
    router.replace('/accounts/withdraw-complete');
  };
  const handleClickWithdraw = async () => {
    await withdrawInputModal.show({
      onSuccessWithdraw
    });
  };
  return <div className="flex w-full items-center justify-center" data-sentry-component="WithdrawPageView" data-sentry-source-file="withdraw.tsx">
      <div className="flex w-[90%] max-w-[1140px] flex-col py-[50px] lg:pb-[100px]">
        <img alt="zep-logo" src="/images/logos/ZEP_LOGO.png" width={80} height={28.39} />
        <Spacing size={30} data-sentry-element="Spacing" data-sentry-source-file="withdraw.tsx" />

        <h1 className="text-h4">
          {t('accounts:accounts.withdraw.headerTitle')}
        </h1>
        <Spacing size={12} data-sentry-element="Spacing" data-sentry-source-file="withdraw.tsx" />
        <p className="text-body-1 text-gray-600">
          {t('accounts:accounts.withdraw.headerDescription')}
        </p>

        <Spacing size={16} data-sentry-element="Spacing" data-sentry-source-file="withdraw.tsx" />
        <Divider data-sentry-element="Divider" data-sentry-source-file="withdraw.tsx" />
        <div className="mt-[30px] flex flex-col gap-[8px] md:mt-[40px] md:flex-row">
          <div className="mt-px flex size-[16px] items-center justify-center rounded-full bg-gray-900">
            <p className="text-caption-2 text-white">1</p>
          </div>
          <div className="flex flex-col gap-[6px]">
            <b className="text-body-1-sb">
              <Trans t={t} values={{
              email: user?.snsId
            }} i18nKey={'accounts:accounts.withdraw.description'} components={{
              E: <span className="text-body-1-sb text-primary" />
            }} data-sentry-element="Trans" data-sentry-source-file="withdraw.tsx" />
            </b>
            <p className="text-body-2 text-gray-600">
              {t('accounts:accounts.withdraw.makeSureText')}
            </p>
          </div>
        </div>

        <Spacing size={30} data-sentry-element="Spacing" data-sentry-source-file="withdraw.tsx" />
        <div className="flex flex-col gap-[8px] md:flex-row">
          <div className="mt-px flex size-[16px] items-center justify-center rounded-full bg-gray-900">
            <p className="text-caption-2 text-white">2</p>
          </div>
          <div className="flex flex-col gap-[6px]">
            <b className="text-body-1-sb">
              {t('accounts:accounts.withdraw.termsAlertText1')}
            </b>
            <p className="text-body-2 text-gray-600">
              {t('accounts:accounts.withdraw.termsAlertText2')}
            </p>

            <Spacing size={6} data-sentry-element="Spacing" data-sentry-source-file="withdraw.tsx" />
            <div className="flex flex-col gap-[8px] rounded-[10px] bg-gray-50 px-[16px] py-[14px]">
              <div className="flex items-center gap-[4px]">
                <ExclamationmarkSmallCircleIcon width={14} height={14} className="text-gray-600" data-sentry-element="ExclamationmarkSmallCircleIcon" data-sentry-source-file="withdraw.tsx" />
                <p className="text-body-2-sb font-bold text-gray-900">
                  {t('accounts:accounts.withdraw.notice')}
                </p>
              </div>
              <div className="flex gap-[8px]">
                <div className="mt-px size-[12px]">
                  <CheckIcon width={12} height={12} className="text-gray-600" data-sentry-element="CheckIcon" data-sentry-source-file="withdraw.tsx" />
                </div>
                <p className="text-caption-1 text-gray-600">
                  {t('accounts:accounts.withdraw.noticeDescription')}
                </p>
              </div>
            </div>
            <Spacing size={12} data-sentry-element="Spacing" data-sentry-source-file="withdraw.tsx" />

            <div className="grid grid-cols-3 md:grid-cols-4">
              <div className="border-r border-t border-gray-300 px-[12px] py-[8px] text-caption-1 text-gray-600">
                {t('accounts:accounts.withdraw.tableHead1')}
              </div>
              <div className="col-span-2 border-t border-gray-300 px-[12px] py-[8px] text-caption-1 text-gray-600 md:col-span-3">
                {t('accounts:accounts.withdraw.tableContent1')}
              </div>
              <div className="border-r border-t border-gray-300 px-[12px] py-[8px] text-caption-1 text-gray-600">
                {t('accounts:accounts.withdraw.tableHead2')}
              </div>
              <div className="col-span-2 border-t border-gray-300 px-[12px] py-[8px] text-caption-1 text-gray-600 md:col-span-3">
                <p>{t('accounts:accounts.withdraw.tableContent2')}</p>
                <p>{t('accounts:accounts.withdraw.tableContent3')}</p>
                <p>{t('accounts:accounts.withdraw.tableContent4')}</p>
              </div>
              <div className="border-r border-t border-gray-300 px-[12px] py-[8px] text-caption-1 text-gray-600">
                {t('accounts:accounts.withdraw.tableHead3')}
              </div>
              <div className="col-span-2 border-t border-gray-300 px-[12px] py-[8px] text-caption-1 text-gray-600 md:col-span-3">
                <p>{t('accounts:accounts.withdraw.tableContent5')}</p>
                <p>{t('accounts:accounts.withdraw.tableContent6')}</p>
              </div>
              <div className="border-y border-r border-gray-300 px-[12px] py-[8px] text-caption-1 text-gray-600">
                {t('accounts:accounts.withdraw.tableHead4')}
              </div>
              <div className="col-span-2 border-y border-gray-300 px-[12px] py-[8px] text-caption-1 text-gray-600 md:col-span-3">
                <p>{t('accounts:accounts.withdraw.tableContent7')}</p>
                <p>{t('accounts:accounts.withdraw.tableContent8')}</p>
              </div>
            </div>
          </div>
        </div>
        <Spacing size={40} data-sentry-element="Spacing" data-sentry-source-file="withdraw.tsx" />
        <Divider data-sentry-element="Divider" data-sentry-source-file="withdraw.tsx" />
        <Spacing size={20} data-sentry-element="Spacing" data-sentry-source-file="withdraw.tsx" />
        <p className="text-body-2-sb font-bold text-[#FF5353]">
          {t('accounts:accounts.withdraw.alertNoticeContent', {
          email: user?.snsId
        })}
        </p>
        <p className="text-body-2-sb font-bold text-[#FF5353]">
          {t('accounts:accounts.withdraw.alertNoticeContent2')}
        </p>
        <Spacing size={20} data-sentry-element="Spacing" data-sentry-source-file="withdraw.tsx" />
        <Checkbox label={t('accounts:accounts.withdraw.checkboxText')} onChange={() => setIsAgreeWithdraw(prev => !prev)} data-sentry-element="Checkbox" data-sentry-source-file="withdraw.tsx" />
        <Spacing size={40} data-sentry-element="Spacing" data-sentry-source-file="withdraw.tsx" />
        <div className="flex gap-[8px]">
          <Button leftSlot={<ChevronLeftIcon width={16} height={16} />} size="sm" variant="sub" onClick={handleClickGoBack} data-sentry-element="Button" data-sentry-source-file="withdraw.tsx">
            {t('accounts:accounts.withdraw.goBack')}
          </Button>
          <Button size="sm" disabled={!isAgreeWithdraw} onClick={handleClickWithdraw} data-sentry-element="Button" data-sentry-source-file="withdraw.tsx">
            {t('accounts:accounts.withdraw.confirmWithdrawButtonText')}
          </Button>
        </div>
      </div>
    </div>;
};